import { DateTime } from 'luxon';

export class DateUtil {
  /**
   * Parses a date. For formats, see https://moment.github.io/luxon/#/parsing?id=table-of-tokens
   * @param date The date to parse.
   * @param format The format to parse the date into.
   * @param fromFormat The format the `date` is expected to be in.
   * @returns The parsed date.
   * @example
   * import { DateUtil } from '@/utils/index';
   *
   * const dateParser = new DateUtil();
   *
   * dateParser.parseDateFromString(
   *   '2023-01-11 08:22:14 -0600',
   *   'L/dd/yyyy',
   *   'yyyy-MM-dd HH:mm:ss ZZZ'
   * );
   */
  parseDateFromString(
    /** Date to parse. */
    date: string,
    /** Desired format. */
    format: string,
    /** The format the 'date' is expected to be in. */
    fromFormat: string | null = null
  ) {
    let dateToValidate = null;
    if (fromFormat) {
      // Used Parsing ruby to_s time date
      dateToValidate = DateTime.fromFormat(date, fromFormat);
    } else {
      dateToValidate = DateTime.fromISO(date);
    }

    if (dateToValidate.isValid) {
      return dateToValidate.toFormat(format);
    }
  }

  yearsSinceDate(date: string, fromFormat: string | null = null) {
    let dateToValidate = null;
    if (fromFormat) {
      dateToValidate = DateTime.fromFormat(date, fromFormat);
    } else {
      dateToValidate = DateTime.fromISO(date);
    }

    if (dateToValidate.isValid) {
      return Math.trunc(DateTime.now().diff(dateToValidate, 'years').years);
    }
  }

  getDaysDiffFromNow(date: string, fromFormat: string | null = null) {
    let dateToCompare = null;
    if (fromFormat) {
      dateToCompare = DateTime.fromFormat(date, fromFormat);
    } else {
      dateToCompare = DateTime.fromISO(date);
    }

    if (dateToCompare.isValid) {
      const currentDate = DateTime.now();
      const diff = dateToCompare.diff(currentDate, 'days');
      return Math.trunc(diff.days);
    }
    return null;
  }

  isAfter(
    dateA: string,
    dateB: string,
    options: { fromFormatA?: string; fromFormatB?: string } = {}
  ) {
    const parseDate = (date: string, format?: string) =>
      format ? DateTime.fromFormat(date, format) : DateTime.fromISO(date);

    const dateToCompare1 = parseDate(dateA, options.fromFormatA);
    const dateToCompare2 = parseDate(dateB, options.fromFormatB);

    return dateToCompare1.isValid && dateToCompare2.isValid
      ? dateToCompare1 > dateToCompare2
      : false;
  }
}
